//* react
import React from 'react';

//* material-ui
import { Alert, Button, CircularProgress, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';

//* types
import { ChatUserModel } from 'types/chat-users/chat-user';

//* system
import * as Yup from 'yup';
import { phoneRegex } from 'utils/regexes';
import { Form, Formik, FormikProps } from 'formik';
import { useSelector } from 'store';
import useLocalization from 'hooks/useLocalization';
import { createChatUser } from 'api/chat-users';
import { getChatUsers } from 'store/slices/chat';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { CompanyPhoneConfig } from 'types/company-platforms/companyPhoneConfig';

interface CreateSingleUserProps {
  /** טקסט מהחיפוש — מוצג בשדה הטלפון */
  initialPhone?: string;
  onBeforeSubmit?: () => void;
  onUserCreated?: (user: ChatUserModel) => void;
  onLoadingChange?: (loading: boolean) => void;
  outerSubmit?: boolean;
}

const buildValidationSchema = (loc: {
  requiredFirstName: string;
  requiredPhone: string;
  invalidPhoneFormat: string;
}) =>
  Yup.object({
    firstName: Yup.string().trim().required(loc.requiredFirstName),
    lastName: Yup.string(),
    phoneNumber: Yup.string()
      .required(loc.requiredPhone)
      .test('phone-format', loc.invalidPhoneFormat, function (value) {
        const { phoneCountryCode } = this.options.context || {};
        if (!value || !String(value).trim()) return true;
        if (phoneCountryCode === 'IL') {
          return phoneRegex.test(value);
        }
        if (!phoneCountryCode) return true;
        return matchIsValidTel(value, {
          onlyCountries: [phoneCountryCode],
        });
      }),
    email: Yup.string().email('Invalid email format'),
    utmSource: Yup.string(),
  });

interface FormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  utmSource: string;
}

const CreateSingleUser = React.forwardRef<
  {
    submit: () => void;
  },
  CreateSingleUserProps
>(({ initialPhone, onUserCreated, onBeforeSubmit, onLoadingChange, outerSubmit }, ref) => {
  //* localization
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => {
    return currentLocale.chat.createUsers;
  }, [currentLocale]);

  const validationSchema = React.useMemo(
    () =>
      buildValidationSchema({
        requiredFirstName: localization.manually.requiredFirstName,
        requiredPhone: localization.manually.requiredPhone,
        invalidPhoneFormat: localization.manually.invalidPhoneFormat,
      }),
    [localization],
  );

  //* Data & states
  const companyPhoneConfigCountryCode = useSelector(
    (state) => state.companyPlatforms.companyPhoneConfig?.countryCode || 'IL',
  );
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  const phoneCountryInfoRef = React.useRef<CompanyPhoneConfig>();
  const initialPhoneField = React.useMemo(() => {
    const raw = initialPhone?.trim() ?? '';
    if (!raw) return '';
    if (companyPhoneConfigCountryCode === 'IL') {
      return raw.replace(/\D/g, '');
    }
    return raw;
  }, [initialPhone, companyPhoneConfigCountryCode]);

  const formikRef =
    React.useRef<
      FormikProps<{ firstName: string; lastName: string; phoneNumber: string; email: string; utmSource: string }>
    >(null);

  React.useImperativeHandle(ref, () => ({
    submit: () => {
      formikRef.current?.submitForm();
    },
  }));

  //* handlers
  const handlePhoneNumberWithCountry = (value: string, telInfo: CompanyPhoneConfig & { reason: string }) => {
    //* =============
    if (telInfo.reason === 'country') {
      phoneCountryInfoRef.current = {
        countryCallingCode: telInfo.countryCallingCode,
        countryCode: telInfo.countryCode,
        nationalNumber: telInfo.nationalNumber,
        numberType: telInfo.numberType,
        numberValue: telInfo.numberValue,
      };
    }
  };

  const handleSubmitCreateUser = async (values: FormValues) => {
    setLoading(true);
    onLoadingChange?.(true);
    setMessage(null); // נקה הודעות קודמות

    const data = await createChatUser(
      values.firstName,
      values.lastName,
      values.phoneNumber,
      values.email,
      phoneCountryInfoRef.current,
      values.utmSource?.trim() ? { utmSource: values.utmSource.trim() } : undefined,
    );
    
    // Debug logging
    console.log('createChatUser response:', data);
    console.log('data type:', typeof data);
    console.log('data keys:', data ? Object.keys(data) : 'null');
    
    if (data && 'UserDisplayPhoneNumber' in data) {
      //TODO add user according to filters instead of requesting all users again
      await getChatUsers(false);
      setMessage({
        text: currentLocale.chat.notifications.createContact.success,
        severity: 'success',
      });
      if (onUserCreated) onUserCreated(data);
    } else if (data) {
      // בדיקה מורחבת לסוגים שונים של שגיאות
      const errorMessage = 'message' in data ? data.message : '';
      const isUserExists = errorMessage.toLowerCase().includes('already exists') || 
                          errorMessage.toLowerCase().includes('user exists') ||
                          errorMessage.toLowerCase().includes('duplicate') ||
                          errorMessage.toLowerCase().includes('קיים');
      
      console.log('Error message:', errorMessage);
      console.log('Is user exists:', isUserExists);
      
      if (isUserExists) {
        setMessage({
          text: currentLocale.chat.notifications.createContact.exists,
          severity: 'error',
        });
      } else {
        setMessage({
          text: currentLocale.chat.notifications.createContact.error + (errorMessage ? `: ${errorMessage}` : ''),
          severity: 'error',
        });
      }
    } else {
      // אם data הוא null או undefined
      setMessage({
        text: currentLocale.chat.notifications.createContact.error,
        severity: 'error',
      });
    }

    setLoading(false);
    onLoadingChange?.(false);
  };

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={{
        firstName: '',
        lastName: '',
        phoneNumber: initialPhoneField,
        email: '',
        utmSource: '',
      }}
      innerRef={formikRef}
      // validationSchema={validationSchema}
      validateOnChange
      validateOnBlur
      onSubmit={handleSubmitCreateUser}
      validate={async (values) => {
        try {
          await validationSchema.validate(values, {
            context: {
              phoneCountryCode: phoneCountryInfoRef?.current?.countryCode || companyPhoneConfigCountryCode,
            },
            abortEarly: false,
          });
          return {};
        } catch (err: unknown) {
          const errors: Partial<FormValues> = {};

          const yerr = err as { inner?: Array<{ path?: string; message: string }>; path?: string; message?: string };
          if (yerr.inner && yerr.inner.length > 0) {
            yerr.inner.forEach((error) => {
              if (error.path) errors[error.path as keyof FormValues] = error.message;
            });
          } else if (yerr.path && yerr.message) {
            errors[yerr.path as keyof FormValues] = yerr.message;
          }
          return errors;
        }
      }}
    >
      {({ values, errors, touched, handleChange, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="firstName"
                label={localization.manually.firstNameLabel}
                value={values.firstName}
                onChange={handleChange}
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName ? String(errors.firstName) : undefined}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="lastName"
                label={localization.manually.lastNameLabel}
                value={values.lastName}
                onChange={handleChange}
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName ? String(errors.lastName) : undefined}
              />
            </Grid>
            <Grid size={12}>
              {companyPhoneConfigCountryCode === 'IL' ? (
                <TextField
                  fullWidth
                  required
                  name="phoneNumber"
                  label={localization.manually.phoneLabel}
                  value={values.phoneNumber}
                  onChange={handleChange}
                  error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                  helperText={touched.phoneNumber && errors.phoneNumber ? String(errors.phoneNumber) : undefined}
                  type="tel"
                />
              ) : (
                <MuiTelInput
                  name="phoneNumber"
                  label={localization.manually.phoneLabel}
                  required
                  value={values.phoneNumber}
                  onChange={(newValue, info) => {
                    handleChange({
                      target: {
                        name: 'phoneNumber',
                        value: newValue,
                      },
                    });
                    handlePhoneNumberWithCountry(newValue, info);
                  }}
                  fullWidth
                  variant="outlined"
                  defaultCountry={companyPhoneConfigCountryCode}
                  forceCallingCode={true}
                  error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                  helperText={touched.phoneNumber && errors.phoneNumber ? String(errors.phoneNumber) : undefined}
                  dir="ltr"
                />
              )}
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                name="email"
                label={localization.manually.emailLabel}
                value={values.email}
                type="email"
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                name="utmSource"
                label={localization.manually.utmSourceLabel}
                value={values.utmSource}
                onChange={handleChange}
                error={touched.utmSource && Boolean(errors.utmSource)}
                helperText={touched.utmSource && errors.utmSource ? String(errors.utmSource) : undefined}
              />
            </Grid>
            {message && (
              <Grid size={12}>
                <Alert severity={message.severity}>
                  {message.text}
                </Alert>
              </Grid>
            )}
            {outerSubmit && (
              <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : undefined}
                  sx={{ minWidth: 150 }}
                >
                  {loading ? 'יוצר איש קשר...' : 'צור איש קשר'}
                </Button>
              </Grid>
            )}
            {!outerSubmit && (
              <>
                {message && (
                  <Grid size={12}>
                    <Alert severity={message.severity}>
                      {message.text}
                    </Alert>
                  </Grid>
                )}
                <Grid size={12} textAlign={'center'}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {localization.manually.create}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Form>
      )}
    </Formik>
  );
});

export default CreateSingleUser;
